import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { adminRequest, hashFile, operationKey } from "lib/adminSiteApi";
import { ErrorNotice } from "./Controls";
const MIME = {
  pdf: "application/pdf",
  zip: "application/zip",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  png: "image/png",
  webp: "image/webp",
  avif: "image/avif",
  mp4: "video/mp4",
  webm: "video/webm",
  mov: "video/quicktime",
};
export default function Upload({ resourceId, courseId, orderId, media = false, onDone, statusInNewTab = false }) {
  const [file, setFile] = useState(null),
    [usage, setUsage] = useState("site"),
    [alt, setAlt] = useState(""),
    [caption, setCaption] = useState(""),
    [state, setState] = useState({ stage: "idle", progress: 0 }),
    [error, setError] = useState(null);
  const work = useRef(null),
    controller = useRef(null);
  useEffect(() => () => controller.current?.abort(), []);
  const busy = ["hashing", "uploading", "finalizing"].includes(state.stage);
  const start = async (e) => {
    e.preventDefault();
    if (!file || busy) return;
    setError(null);
    const ctrl = new AbortController();
    controller.current = ctrl;
    try {
      const mime = MIME[file.name.split(".").pop().toLowerCase()];
      const max = courseId ? (mime?.startsWith("video/") ? 750 : 50) : media ? (mime?.startsWith("video/") ? 750 : 8) : 50;
      if (
        !mime ||
        !file.size ||
        file.size > max * 1024 * 1024 ||
        (courseId ? !["application/pdf","application/zip","video/mp4","video/webm"].includes(mime) : media
          ? mime.startsWith("application/")
          : !mime.startsWith("application/"))
      )
        throw new Error(
          `Choose ${media ? "an image (up to 8 MiB) or overview video (up to 750 MiB)" : "a PDF or ZIP (up to 50 MiB)"}.`,
        );
      if (
        media &&
        mime.startsWith("video/") &&
        usage !== "webinar-overview-video"
      )
        throw new Error("Choose Webinar overview video for video files.");
      work.current ||= {
        beginKey: operationKey(),
        finalKey: operationKey(),
        next: 0,
      };
      const w = work.current;
      if (!w.checksum) {
        setState({ stage: "hashing", progress: 0 });
        w.checksum = await hashFile(
          file,
          (p) => setState({ stage: "hashing", progress: Math.round(p * 100) }),
          ctrl.signal,
        );
      }
      if (!w.session) {
        const body = media
          ? {
              name: file.name,
              mime,
              size: file.size,
              checksum: w.checksum,
              usage,
              altText: alt,
              caption,
            }
          : {
              ...(courseId || orderId ? {} : { resourceId }),
              fileName: file.name,
              mime,
              size: file.size,
              checksum: w.checksum,
            };
        w.session = (
          await adminRequest(
            orderId ? `/admin/commerce/orders/${orderId}/delivery/uploads` : courseId ? `/admin/course-delivery/${courseId}/uploads` : media ? "/admin/media/uploads" : "/admin/uploads",
            { method: "POST", body, key: w.beginKey, signal: ctrl.signal },
          )
        ).data;
      }
      const session = w.session;
      for (let i = w.next; i < session.parts; i++) {
        setState({
          stage: "uploading",
          progress: Math.round((i / session.parts) * 100),
          id: session.id,
        });
        await adminRequest(`/admin/versions/${session.id}/parts/${i}`, {
          method: "PUT",
          body: file.slice(i * session.partBytes, (i + 1) * session.partBytes),
          binary: true,
          signal: ctrl.signal,
        });
        w.next = i + 1;
      }
      setState({ stage: "finalizing", progress: 100, id: session.id });
      await adminRequest(`/admin/versions/${session.id}/finalize`, {
        method: "POST",
        body: {},
        key: w.finalKey,
        signal: ctrl.signal,
      });
      setState({ stage: "quarantine", progress: 100, id: session.id });
      onDone?.(session.id);
    } catch (err) {
      setState((s) => ({
        ...s,
        stage: err.name === "AbortError" ? "paused" : "failed",
      }));
      if (err.name !== "AbortError") setError(err);
    }
  };
  return (
    <section className="aw-panel">
      <h2>{orderId ? "Upload private order file" : courseId ? "Upload private course file" : media ? "Upload public media" : "Upload a file version"}</h2>
      <p className="aw-muted">
        {orderId ? "PDF and ZIP files stay private to this order. Validate, select and review the prepared files before releasing them to the verified customer." : courseId ? "Course PDF, ZIP, MP4 and WebM files stay private and require enrolled access. Validation must finish before use." : media
          ? "Images and overview videos are public only after checks pass and you publish them."
          : "PDF and ZIP files stay private. File validation must finish before publication. Replacing a file creates a new version."}
      </p>
      <form onSubmit={start}>
        <fieldset disabled={busy || state.stage === "quarantine"}>
          <label>
            Choose file
            <input
              type="file"
              accept={
                courseId ? ".pdf,.zip,.mp4,.webm" : media
                  ? ".jpg,.jpeg,.png,.webp,.avif,.mp4,.webm,.mov"
                  : ".pdf,.zip"
              }
              required
              onChange={(e) => {
                setFile(e.target.files[0] || null);
                work.current = null;
                setState({ stage: "idle", progress: 0 });
                setError(null);
              }}
            />
          </label>
          {media && (
            <div className="aw-fields">
              <label>
                Usage
                <select
                  value={usage}
                  disabled={!!work.current?.session}
                  onChange={(e) => setUsage(e.target.value)}
                >
                  {[
                    "site",
                    "blog",
                    "hero",
                    "webinar",
                    "resource",
                    "careers",
                    "webinar-overview-video",
                  ].map((v) => (
                    <option key={v}>{v}</option>
                  ))}
                </select>
              </label>
              <label>
                Alternative text
                <input
                  value={alt}
                  maxLength={1000}
                  disabled={!!work.current?.session}
                  onChange={(e) => setAlt(e.target.value)}
                />
              </label>
              <label>
                Caption
                <input
                  value={caption}
                  maxLength={4000}
                  disabled={!!work.current?.session}
                  onChange={(e) => setCaption(e.target.value)}
                />
              </label>
            </div>
          )}
          <button
            className="aw-button aw-primary"
            disabled={!file || busy}
            type="submit"
          >
            {work.current ? "Resume / retry upload" : "Upload for validation"}
          </button>
        </fieldset>
      </form>
      {busy && (
        <button
          className="aw-button"
          onClick={() => controller.current?.abort()}
        >
          Pause upload
        </button>
      )}
      <div role="status" className="aw-upload-status">
        {state.stage !== "idle" && (
          <>
            <progress
              max={100}
              value={state.progress}
              aria-label="Upload progress"
            />
            <p>
              {state.stage === "quarantine"
                ? "Uploaded. Waiting for file validation."
                : `${state.stage} · ${state.progress}%`}
            </p>
          </>
        )}
        {state.id && (
          <Link className="aw-link" to={`/admin/versions/${state.id}`} target={statusInNewTab ? "_blank" : undefined} rel={statusInNewTab ? "noreferrer" : undefined}>
            View file status
          </Link>
        )}
      </div>
      <ErrorNotice error={error} />
      {state.stage === "quarantine" && (
        <button
          className="aw-button"
          onClick={() => {
            work.current = null;
            setFile(null);
            setState({ stage: "idle", progress: 0 });
          }}
        >
          Upload another file
        </button>
      )}
    </section>
  );
}
